import { useForm as useFormHook } from "hooks";
import type { HighRiskMerchant } from 'features/ecommerce/types';
import {
    FormField,
    type OnboardingUploadDocuments,
    type OnboardingUploadDocumentsForm
} from "./types";
import useValidationSchema from "./useValidationSchema";
import { DocumentIdentity } from "./entities";

export const getInitialValues = ({
    clientDocuments = []
}: Partial<HighRiskMerchant.OnboardingUploadDocuments>): OnboardingUploadDocuments => {
    const map = new Map<HighRiskMerchant.Document['documentType'], Array<DocumentIdentity>>();

    for (const clientDocument of clientDocuments) {
        const documentType = clientDocument.documentType;

        if (!documentType) {
            continue;
        }

        map.set(
            documentType,
            [
                ...(map.get(documentType) ?? []),
                new DocumentIdentity(clientDocument)
            ]
        );
    }

    return Array.from([
        FormField.CertificateOfIncorporation,
        FormField.MemorandumAndArticles,
        FormField.CompanyAccountBankStatement,
        FormField.CompanyFinancialStatement,
        FormField.BankReference,
        FormField.CopyOfLicences,
        FormField.ProofOfDomainOwnership,
        FormField.PassportsOfShareholders,
        FormField.UtilityBillOfShareholders,
        FormField.OfficialCompanyDocWithAuthority,
        FormField.StructureChart
    ].reduce((map, documentType) => {
        if (!map.has(documentType)) {
            return map.set(documentType, [
                new DocumentIdentity({ documentType })
            ]);
        }

        return map;
    }, map).entries())
        .reduce((obj, [key, value]) => ({
            ...obj,
            [key]: value
        }), {} as Record<HighRiskMerchant.Document['documentType'], Array<DocumentIdentity>>);
};

export const useForm = (onboardingUploadDocuments: OnboardingUploadDocumentsForm) =>
    useFormHook({
        isInitialValid: false,
        ...onboardingUploadDocuments,
        validateOnMount: true,
        validationSchema: useValidationSchema()
    });
